import { useTableSort } from '@flexinet/ui-components';
import { DataTableSortStatus } from 'mantine-datatable';

import { usePhasesQuery } from './usePhasesQuery';
import { usePhasesSearch } from './usePhasesSearch';
import { usePhasesTableColumns } from './usePhasesTableColumns';
import { Phase } from '../types';

// DEFAULT: by `position` ASCENDING — deliberately NOT the newest-first default
// the checklist prescribes for a generic list.
//
// A phase's position is its place in the playbook's sequence: phase 1 runs
// before phase 2. That ordering is the entity's meaning, and an operator
// reading a playbook expects to see its phases in the order they execute.
// Sorting them newest-first would scramble a sequence into creation order.
//
// `position: number` is non-null and immutable in practice (it changes only
// when someone deliberately reorders the playbook), which satisfies the real
// requirement behind the newest-first rule — a stable, non-null key that does
// not move under the reader. `created` is nullable on Phase and would degrade
// to insertion order if rows lack it.
const DEFAULT_SORT_STATUS: DataTableSortStatus<Phase> = {
    columnAccessor: 'position',
    direction: 'asc',
};

export const usePhases = (filters: Record<string, string> = {}) => {
    const query = usePhasesQuery(filters);
    const { keyword } = usePhasesSearch();
    const { columns } = usePhasesTableColumns();

    const items: Phase[] = query.data?.pages?.flatMap(page =>
        (page && 'items' in page) ? page.items : []
    ) ?? [];

    // ⚠️ Both the keyword filter and the sort below cover only the pages
    // FETCHED SO FAR. Under token pagination the browser cannot filter or
    // reorder rows it has never seen, so the footer states a LOADED count
    // rather than a total, and `unfilteredCount` is reported alongside the
    // match count so the operator can see the search was scoped to a window.
    const needle = keyword.trim().toLowerCase();

    const matched = items.filter(item =>
        !needle ||
        item.name?.toLowerCase().includes(needle) ||
        item.displayName?.toLowerCase().includes(needle) ||
        item.description?.toLowerCase().includes(needle)
    );

    // Sort state is component-level, not the module-scoped store: sort is how
    // ONE operator is viewing the table right now, unlike the keyword and the
    // filters, which are shared with sibling components.
    const {
        sortStatus,
        setSortStatus,
        sorted: visibleItems,
    } = useTableSort<Phase>(matched, DEFAULT_SORT_STATUS);

    const fetchNextPage = () => {
        if (query.hasNextPage) {
            query.fetchNextPage();
        }
    };

    return {
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingNextPage: query.isFetchingNextPage,
        unfilteredCount: items.length,
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchNextPage,
        columns,
    };
};
